import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Avatar, PriorityFlag } from '@/components/Chips'
import { Icon } from '@/components/Icon'
import type { WorkItem } from '@/types/work'

/**
 * ISO-8601 day numbers AWJ works: Sunday through Thursday.
 *
 * The same definition the business calendar uses on the server (ADR-0009).
 * Friday and Saturday still appear — work does slip onto them and hiding the
 * column would hide the task — but they are drawn as non-working, so a date
 * landing there reads as the exception it is.
 */
const WORKING_DAYS = [7, 1, 2, 3, 4]

/** Columns run Sunday-first, because that is where the Omani week starts. */
const WEEK = [7, 1, 2, 3, 4, 5, 6]

/**
 * The calendar layout.
 *
 * `View::LAYOUTS` has allowed `calendar` since Phase 1 with nothing to draw it.
 * It answers the question the list cannot: not "what is outstanding" but "what
 * lands the same week as what" — which is the question asked when a deadline
 * moves.
 *
 * Undated items are shown beneath rather than dropped. A month grid that
 * silently omits half the project is the same class of bug as a list that
 * stops at fifty.
 */
export function TaskCalendar({
  items,
  onOpen,
}: {
  items: WorkItem[]
  onOpen: (item: WorkItem) => void
}) {
  const { t, i18n } = useTranslation()
  const [offset, setOffset] = useState(0)

  const { days, label, undated } = useMemo(() => {
    const dated = items.filter((item) => item.dueDate !== null)
    const anchor = new Date()
    anchor.setDate(1)
    anchor.setMonth(anchor.getMonth() + offset)

    const year = anchor.getFullYear()
    const month = anchor.getMonth()

    const first = new Date(year, month, 1)
    const total = new Date(year, month + 1, 0).getDate()

    // Sunday-first: how many blanks before the 1st.
    const lead = (first.getDay() + 7) % 7

    const cells: { date: Date | null; items: WorkItem[] }[] = []

    for (let i = 0; i < lead; i++) cells.push({ date: null, items: [] })

    for (let day = 1; day <= total; day++) {
      const date = new Date(year, month, day)
      const iso = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`

      cells.push({
        date,
        items: dated.filter((item) => item.dueDate?.slice(0, 10) === iso),
      })
    }

    return {
      days: cells,
      label: first.toLocaleDateString(i18n.language, { month: 'long', year: 'numeric' }),
      undated: items.filter((item) => item.dueDate === null),
    }
  }, [items, offset, i18n.language])

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setOffset((value) => value - 1)}
          aria-label={t('view.previousMonth')}
          className="text-ink-500 hover:bg-surface-2 hover:text-ink-900 rounded p-1.5"
        >
          <Icon name="chevron" className="size-3.5 rotate-180 rtl:rotate-0" />
        </button>
        <span className="text-ink-900 min-w-40 text-center text-sm font-medium">{label}</span>
        <button
          type="button"
          onClick={() => setOffset((value) => value + 1)}
          aria-label={t('view.nextMonth')}
          className="text-ink-500 hover:bg-surface-2 hover:text-ink-900 rounded p-1.5"
        >
          <Icon name="chevron" className="size-3.5 rtl:rotate-180" />
        </button>
        {offset === 0 ? null : (
          <button
            type="button"
            onClick={() => setOffset(0)}
            className="text-brand-600 text-[12px]"
          >
            {t('view.thisMonth')}
          </button>
        )}
      </div>

      <div className="border-line bg-line grid grid-cols-7 gap-px overflow-hidden rounded-md border">
        {WEEK.map((day) => (
          <div
            key={day}
            className={`px-2 py-1.5 text-center font-mono text-[10px] tracking-widest uppercase ${
              WORKING_DAYS.includes(day) ? 'bg-surface-2 text-ink-500' : 'bg-surface-2 text-ink-500/60'
            }`}
          >
            {new Date(2026, 10, 8 + WEEK.indexOf(day)).toLocaleDateString(i18n.language, {
              weekday: 'short',
            })}
          </div>
        ))}

        {days.map((cell, index) => {
          const iso = cell.date === null ? 'blank' : cell.date.toISOString()
          const weekday = cell.date === null ? 0 : ((cell.date.getDay() + 6) % 7) + 1
          const working = WORKING_DAYS.includes(weekday)

          return (
            <div
              key={`${iso}-${index}`}
              className={`min-h-24 p-1.5 ${
                cell.date === null ? 'bg-surface-2/40' : working ? 'bg-surface' : 'bg-surface-2/60'
              }`}
            >
              {cell.date === null ? null : (
                <>
                  <span
                    className={`font-mono text-[10px] tabular-nums ${
                      working ? 'text-ink-500' : 'text-ink-500/60'
                    }`}
                  >
                    {cell.date.getDate()}
                  </span>

                  <ul className="mt-1 flex flex-col gap-1">
                    {cell.items.map((item) => (
                      <li key={item.id}>
                        <button
                          type="button"
                          onClick={() => onOpen(item)}
                          className="border-line hover:bg-surface-2 flex w-full items-center gap-1 rounded border px-1 py-0.5 text-start"
                        >
                          <PriorityFlag priority={item.priority} />
                          <span className="text-ink-900 min-w-0 flex-1 truncate text-[11px]">
                            {item.title}
                          </span>
                          <Avatar
                            name={item.assignee?.name ?? null}
                            color={item.assignee?.color}
                            size="sm"
                          />
                        </button>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          )
        })}
      </div>

      {undated.length === 0 ? null : (
        <section>
          <h3 className="text-ink-500 mb-1.5 font-mono text-[10px] tracking-widest uppercase">
            {t('view.undated', { count: undated.length })}
          </h3>
          <ul className="flex flex-wrap gap-1.5">
            {undated.map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  onClick={() => onOpen(item)}
                  className="border-line bg-surface hover:bg-surface-2 flex items-center gap-1.5 rounded border px-2 py-1 text-[12px]"
                >
                  <PriorityFlag priority={item.priority} />
                  <span className="text-ink-900 max-w-48 truncate">{item.title}</span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  )
}
